import { useId, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { RiArrowDownSLine } from 'react-icons/ri'
import type { DashboardStatusFilterOption } from '@/features/dashboard/components/DashboardStatusFilter'
import {
  SP_LIST_DEMOGRAPHIC_DROPDOWN_BORDER,
  SP_LIST_DEMOGRAPHIC_DROPDOWN_HEIGHT_PX,
  SP_LIST_DEMOGRAPHIC_FILTER_COL_WIDTH_PX,
  SP_LIST_DEMOGRAPHIC_FILTER_LABEL_GAP_PX,
  SP_LIST_DEMOGRAPHIC_LABEL_COLOR,
  SP_LIST_DEMOGRAPHIC_VALUE_COLOR,
} from '../lib/signal-provider-filter-layout'

interface Props<T extends string> {
  label: string
  value: T
  onChange: (value: T) => void
  options: readonly DashboardStatusFilterOption<T>[]
  testId: string
}

/** Figma `37:792` — stacked uppercase label + compact 36px dropdown. */
export function SignalProviderDemographicFilterField<T extends string>({
  label,
  value,
  onChange,
  options,
  testId,
}: Readonly<Props<T>>) {
  const labelId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const selectedLabel = options.find((o) => o.value === value)?.label ?? 'Any'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${SP_LIST_DEMOGRAPHIC_FILTER_LABEL_GAP_PX}px`,
        width: '100%',
        maxWidth: SP_LIST_DEMOGRAPHIC_FILTER_COL_WIDTH_PX,
      }}
    >
      <Typography
        id={labelId}
        component="label"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          lineHeight: '16.5px',
          letterSpacing: '0.275px',
          textTransform: 'uppercase',
          color: SP_LIST_DEMOGRAPHIC_LABEL_COLOR,
        }}
      >
        {label}
      </Typography>

      <ButtonBase
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          width: '100%',
          height: SP_LIST_DEMOGRAPHIC_DROPDOWN_HEIGHT_PX,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: open ? '#008cd0' : SP_LIST_DEMOGRAPHIC_DROPDOWN_BORDER,
          bgcolor: '#ffffff',
          px: '9px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          textAlign: 'left',
          '&:hover': { bgcolor: '#ffffff' },
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            color: SP_LIST_DEMOGRAPHIC_VALUE_COLOR,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedLabel}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            flexShrink: 0,
            color: SP_LIST_DEMOGRAPHIC_LABEL_COLOR,
          }}
          aria-hidden
        >
          <RiArrowDownSLine size={16} />
        </Box>
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: '4px',
              minWidth: anchorEl?.offsetWidth ?? SP_LIST_DEMOGRAPHIC_FILTER_COL_WIDTH_PX,
              borderRadius: '10px',
              boxShadow: '0px 4px 12px rgba(13, 27, 42, 0.12)',
            },
          },
        }}
        MenuListProps={{ role: 'listbox', 'aria-labelledby': labelId }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: 12,
              lineHeight: '16px',
              color: SP_LIST_DEMOGRAPHIC_VALUE_COLOR,
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
